/**
 * PenTool.tsx
 * HTML5 Canvas drawing layer for Pen, Highlighter, and Eraser tools.
 * Rendered as a full-screen transparent overlay during presenter mode.
 */

import React, { useEffect, useCallback, type RefObject } from 'react';
import { usePresenterStore } from '@store/presenterStore';

interface PenToolProps {
  canvasRef: RefObject<HTMLCanvasElement>;
}

export default function PenTool({ canvasRef }: PenToolProps): React.ReactElement {
  const { activeTool, penStyle, highlighterStyle, pushCanvasSnapshot } = usePresenterStore();

  // Set canvas size to window size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const handleResize = () => {
      // Save current drawing, resize, restore
      const dataUrl = canvas.toDataURL();
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = dataUrl;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [canvasRef]);

  const startDrawing = useCallback((e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    canvas.setPointerCapture(e.pointerId);
    ctx.beginPath();
    ctx.moveTo(e.clientX, e.clientY);

    const style = activeTool === 'highlighter' ? highlighterStyle : penStyle;
    ctx.strokeStyle = style.color;
    ctx.lineWidth = style.width;
    ctx.globalAlpha = style.opacity;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (activeTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = 32;
    } else {
      ctx.globalCompositeOperation = 'source-over';
    }
  }, [activeTool, canvasRef, penStyle, highlighterStyle]);

  const draw = useCallback((e: React.PointerEvent) => {
    if (e.buttons !== 1) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.lineTo(e.clientX, e.clientY);
    ctx.stroke();
  }, [canvasRef]);

  const endDrawing = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    pushCanvasSnapshot(canvas.toDataURL());
  }, [canvasRef, pushCanvasSnapshot]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[9010] pointer-events-auto"
      style={{ cursor: activeTool === 'eraser' ? 'cell' : 'crosshair' }}
      onPointerDown={startDrawing}
      onPointerMove={draw}
      onPointerUp={endDrawing}
    />
  );
}
